import type { en } from "./en";

export const id: typeof en = {
  common: {
    add: "Tambah",
    signOut: "Keluar",
    noPhoto: "Tidak ada foto",
    unassigned: "Belum ditempatkan",
    noRecordsHereYet: "Belum ada rekaman di sini.",
    recordsCount: (n: number) => `${n} rekaman`,
    roleAdmin: "admin",
    roleUser: "pengguna",
    paginationPrevious: "Sebelumnya",
    paginationNext: "Berikutnya",
    pageOf: (page: number, total: number) => `Halaman ${page} dari ${total}`,
  },
  types: {
    CD: "CD",
    CASSETTE: "Kaset",
    VINYL: "Vinil",
  },
  nav: {
    collection: "Koleksi",
    artists: "Artis",
    browse: "Jelajahi",
    books: "Buku",
    settings: "Pengaturan",
    profileSettings: "Profil & pengaturan",
  },
  notFound: {
    eyebrow: "Tidak ada di katalog",
    title: "Halaman ini tidak ada di rak.",
    body: "Tautannya mungkin sudah kedaluwarsa, atau alamatnya salah ketik. Koleksi Anda aman. Tidak ada yang terhapus.",
    home: "Kembali ke beranda",
    contactPrefix: "Masih belum ketemu?",
    shelfAlt: "Rak berisi punggung piringan dengan satu slot kosong",
  },
  nativeAuthReturn: {
    eyebrow: "Masuk berhasil",
    title: "Anda sudah masuk.",
    body: "Halaman ini terbuka di peramban, bukan di aplikasi Rakemori. Akun Anda baik-baik saja — kembali ke aplikasi dan pilih Lanjutkan dengan Google sekali lagi untuk menyelesaikannya di sana.",
    browserNote: "Membaca ini di komputer? Anda sudah masuk di peramban ini.",
    home: "Buka perpustakaan saya",
    iosBody:
      "Halaman ini terbuka di Safari, bukan di aplikasi Rakemori, dan di iPhone mencoba lagi hanya akan kembali ke sini \u2014 build ini tidak dapat mengembalikan proses masuk Google ke aplikasi. Tidak ada yang salah dengan akun Anda. Untuk memakai aplikasinya, buka aplikasi lalu masuk dengan nama pengguna dan kata sandi Anda.",
    iosBodyNoPassword:
      "Halaman ini terbuka di Safari, bukan di aplikasi Rakemori, dan di iPhone mencoba lagi hanya akan kembali ke sini \u2014 build ini tidak dapat mengembalikan proses masuk Google ke aplikasi. Tidak ada yang salah dengan akun Anda: akun itu hanya belum punya kata sandi, dan kata sandi adalah cara masuk di dalam aplikasi.",
    iosAddPassword: "Tambahkan kata sandi",
  },
  login: {
    subtitle: "Masuk ke perpustakaan Anda",
    username: "Nama pengguna",
    password: "Kata sandi",
    signIn: "Masuk",
    signingIn: "Sedang masuk…",
    invalidCredentials: "Nama pengguna atau kata sandi salah",
    tooManyAttempts: "Terlalu banyak percobaan masuk. Tunggu beberapa menit, lalu coba lagi.",
    orDivider: "atau",
    signInWithGoogle: "Lanjutkan dengan Google",
    signInWithApple: "Lanjutkan dengan Apple",
    googleNotLinked: "Akun Google ini belum tertaut ke akun Rakemori. Masuk di bawah ini, lalu tautkan Google dari Pengaturan.",
    appleNotLinked: "Akun Apple ini belum tertaut ke akun Rakemori. Masuk di bawah ini, lalu tautkan Apple dari Pengaturan.",
    emailAlreadyRegistered:
      "Akun untuk email ini sudah ada. Masuk di bawah ini, lalu tautkan Google dari Pengaturan.",
    registrationClosed: "Pendaftaran sedang ditutup.",
    googleEmailUnverified:
      "Google tidak mengonfirmasi alamat email untuk akun tersebut, jadi kami tidak bisa membuat akun di sini.",
    registrationRateLimited: "Terlalu banyak percobaan pendaftaran. Coba lagi nanti.",
    registrationFailed: "Terjadi kesalahan saat membuat akun Anda. Silakan coba lagi.",
  },
  welcome: {
    setupTitle: "Siapkan akun Anda",
    addressIntro: (username: string) =>
      `Koleksi Anda berada di rakemori.com/${username}. Anda bisa mengubahnya nanti di Pengaturan.`,
    passwordTitle: "Tambahkan kata sandi",
    passwordIntro:
      "Akun Anda hanya bisa masuk dengan Google atau Apple. Dengan menambahkan kata sandi, Anda tetap bisa masuk jika suatu saat kehilangan akses ke akun tersebut — di sini tidak ada email pemulihan.",
    username: "Nama pengguna",
    password: "Kata sandi",
    confirmPassword: "Konfirmasi kata sandi",
    policyHint: (min: number) => `Minimal ${min} karakter, dengan satu huruf kapital dan satu angka.`,
    continueAction: "Lanjutkan",
    saving: "Menyimpan…",
    signOut: "Keluar",
    passwordMismatch: "Kata sandi tidak cocok.",
    failed: "Tidak bisa menyelesaikan penyiapan akun Anda.",
  },
  offline: {
    title: "Anda sedang offline",
    body: "Rakemori membutuhkan koneksi jaringan untuk memuat katalog Anda. Periksa koneksi lalu coba lagi.",
    browseOffline: "Jelajahi pustaka Anda secara offline",
    heading: "Pustaka offline",
    syncedAt: "Disinkronkan",
    notDownloaded:
      "Belum ada salinan offline di perangkat ini. Buka aplikasi sekali saat online dan salinan akan terunduh otomatis.",
    searchPlaceholder: "Cari judul, artis…",
    empty: "Tidak ada yang cocok.",
    backOnline: "Anda kembali online — muat ulang untuk kembali ke aplikasi.",
    reload: "Muat ulang",
    partialImages: "Penyimpanan penuh — sebagian gambar ukuran penuh tidak tersedia offline.",
    all: "Semua",
    itemsTab: "Koleksi",
    booksTab: "Buku",
    browseTab: "Jelajah",
    detail: {
      tracklist: "Daftar lagu",
      notes: "Catatan",
      catalogNotes: "Catatan katalog",
      location: "Lokasi",
      borrowedBy: "Dipinjam oleh",
      media: "Media",
      sleeve: "Sampul",
      streamingOffline: "Tautan streaming membutuhkan koneksi",
    },
    browse: {
      artists: "Artis",
      genres: "Genre",
      styles: "Gaya",
      countries: "Negara",
    },
  },
  landing: {
    nav: {
      what: "Fitur",
      how: "Cara kerja",
      where: "Lokasi",
      app: "Aplikasi Android",
      faq: "Tanya jawab",
      dashboard: "Dasbor",
    },
    hero: {
      eyebrow: "Katalog pribadi",
      title: "Rakemori",
      tagline: "Tahu apa yang Anda punya, dan di mana.",
      body: "Rakemori adalah katalog pribadi untuk CD, kaset, piringan hitam, dan buku Anda. Katalog ini mencatat di mana letaknya, siapa yang meminjam, dan apa saja yang Anda tambahkan atau lepas.",
      formatCd: "CD",
      formatCs: "Kaset",
      formatVi: "Piringan",
      formatBk: "Buku",
      searchPlaceholder: "Cari judul atau artis…",
      scan: "Pindai",
    },
    features: {
      heading: "Dibuat mengikuti cara koleksi bertambah.",
      highlight: {
        badge: "Baru",
        title: "Dengarkan dulu, baru simpan",
        body: "Pindai sebuah piringan dan setiap lagunya punya tombol putar sebelum Anda menyimpan. Cuplikan 30 detiknya diambil dari iTunes dan Deezer, jadi Anda bisa memastikan hasil pencocokannya benar sebelum masuk ke pustaka. Tombol putarnya tetap ada di daftar lagu setelah tersimpan.",
        nowPlaying: "Sedang diputar",
      },
      items: [
        {
          title: "Katalogisasi",
          body: "Setiap CD, kaset, piringan, dan buku dalam satu tempat. Tambahkan manual, atau biarkan detailnya terisi otomatis.",
        },
        {
          title: "Pindai barcode & isi otomatis",
          body: "Pindai barcode atau cari berdasarkan judul dan artis. Sampul dan daftar lagu diambil dari Discogs, Open Library, dan Google Books.",
        },
        {
          title: "Pelacakan lokasi fisik",
          body: "Buat peta bertingkat lokasi barang Anda, dari rumah sampai ke rak tempatnya disimpan.",
        },
        {
          title: "Berbagi, pinjam & riwayat",
          body: "Bagikan pustaka Anda dalam mode baca-saja, dan catat apa yang dipinjam serta oleh siapa.",
        },
      ],
    },
    how: {
      heading: "Dari kardus ke katalog.",
      steps: [
        {
          title: "Pindai",
          body: "Arahkan kamera ke barcode, atau cari berdasarkan judul dan artis. Sampul dan daftar lagunya langsung terisi, lengkap dengan tombol putar di setiap lagu.",
        },
        {
          title: "Katalog",
          body: "Langsung masuk ke pustaka Anda. Ubah apa saja atau tambahkan catatan; kini catatan itu milik Anda.",
        },
        {
          title: "Temukan",
          body: "Tandai lokasinya, hingga ke rak atau kardus, agar mudah ditemukan nanti.",
        },
      ],
    },
    where: {
      eyebrow: "Peta bertingkat",
      heading: "Tak ada lagi yang hilang di kardus.",
      body: "Bangun pohon lokasi sedalam rumah Anda: rumah, ruang, rak, peti. Setiap barang menunjuk tepat ke tempatnya, jadi Anda tahu ke mana harus mengembalikannya.",
      badges: {
        house: "Rumah",
        livingRoom: "Ruang Tamu",
        shelfB: "Rak B",
      },
      tree: {
        house: "Rumah",
        livingRoom: "Ruang Tamu",
        shelfB: "Rak B",
        shelfBItems: "42 barang",
        cabinet: "Lemari",
        cabinetItems: "16 barang",
        study: "Ruang Kerja",
        boxes: "Kotak 1–3",
        boxesItems: "31 barang",
        loft: "Loteng",
        crate: "Peti",
        crateItems: "27 barang",
      },
    },
    stats: {
      stat1: "Format: CD · Kaset · Piringan · Buku",
      stat2: "Tiga sumber isi otomatis",
      stat3: "Pustaka pribadi, milik Anda sendiri",
    },
    privacy: {
      eyebrow: "Pribadi sejak awal",
      heading: "Pribadi sejak awal, dibagikan sesuai keinginan Anda.",
      body: "Pustaka Anda bersifat pribadi dan menjadi milik Anda. Anda yang menentukan apakah orang lain boleh melihatnya.",
      items: [
        {
          title: "Tidak ada yang publik",
          body: "Tidak ada daftar publik. Tidak ada yang terlihat sampai Anda mengizinkannya.",
        },
        {
          title: "Berbagi baca-saja",
          body: "Kirim tautan yang bisa dilihat tetapi tidak bisa diubah. Cabut kapan saja.",
        },
        {
          title: "Pinjaman & riwayat lengkap",
          body: "Lihat siapa memegang apa, dan telusuri semua yang pernah Anda tambahkan atau lepas.",
        },
      ],
    },
    faq: {
      eyebrow: "Pertanyaan",
      heading: "Perlu Anda tahu.",
      items: [
        {
          q: "Format apa saja yang bisa saya katalogkan?",
          a: "CD, kaset, piringan hitam, dan buku, semuanya dalam satu pustaka.",
        },
        {
          q: "Dari mana detailnya berasal?",
          a: "Sampul, daftar lagu, dan metadata berasal dari Discogs, Open Library, dan Google Books.",
        },
        {
          q: "Bisakah orang lain melihat pustaka saya?",
          a: "Hanya jika Anda membagikannya. Tautan berbagi bersifat baca-saja dan bisa dicabut kapan pun.",
        },
        {
          q: "Apakah saya perlu akun?",
          a: "Ya. Daftar dengan akun Google Anda, atau minta nama pengguna dan kata sandi. Pustaka Anda tetap pribadi, dan hanya Anda yang bisa membukanya.",
        },
        {
          q: "Bagaimana dengan barang yang saya pinjamkan?",
          a: "Tandai barang sebagai dipinjam dan kepada siapa; riwayat peminjaman lengkap tersimpan pada catatannya.",
        },
        {
          q: "Bagaimana jika saya menghapus sesuatu?",
          a: "Barang keluar dari rak aktif tetapi tetap ada di riwayat, jadi Anda selalu tahu apa yang pernah dimiliki.",
        },
        {
          q: "Apakah ada aplikasi seluler?",
          a: "Ada. Rakemori tersedia di Google Play untuk Android, dan tetap bisa dibuka lewat browser mana pun di ponsel maupun komputer. Versi iPhone sedang dalam perjalanan ke App Store. Akun dan pustakanya sama.",
        },
      ],
    },
    getApp: {
      eyebrow: "Di Google Play",
      heading: "Rakemori, di saku Anda.",
      body: "Aplikasi Android membawa seluruh katalog Anda ke depan rak yang sedang Anda hadapi, dengan pemindai barcode sekali ketuk. Aplikasi ini masuk ke pustaka yang sama dengan versi webnya.",
      cta: "Dapatkan di Google Play",
      ctaSub: "Gratis · Android 7.0 ke atas",
      apkCta: "Unduh APK",
      apkNote:
        "Untuk ponsel tanpa Play Store. Android akan meminta izin sebelum memasang, dan Anda kembali ke sini saat ada versi baru.",
      webCta: "Atau buka lewat browser",
      altStoreHeading: "iPhone, lewat AltStore",
      altStoreCta: "Salin URL sumber",
      altStoreCopied: "Tersalin",
      altStoreSelected: "Terpilih — silakan salin",
      altStoreNote:
        "Tempelkan ini ke tab Sources di AltStore. AltStore menandatangani aplikasi dengan Apple ID Anda sendiri, jadi pemasangan pertama memerlukan komputer yang menjalankan AltServer, dan Apple ID gratis harus memperbaruinya setiap tujuh hari. Tautan ke rakemori.com terbuka di Safari, bukan di aplikasi.",
      altStoreGuide: "Panduan pemasangan langkah demi langkah",
      ios: "App Store · segera hadir",
      phoneAlt: "Aplikasi Rakemori berjalan di ponsel Android",
      points: [
        {
          title: "Pindai dengan kamera",
          body: "Arahkan ke barcode, lalu sampul, artis, dan daftar lagu langsung terisi sendiri.",
        },
        {
          title: "Satu pustaka, dua tempat",
          body: "Apa yang Anda tambahkan di ponsel sudah ada saat Rakemori dibuka di laptop.",
        },
        {
          title: "Masuk sesuai selera",
          body: "Masuk dengan Google, atau dengan nama pengguna dan kata sandi. Semuanya tetap pribadi.",
        },
      ],
    },
    footer: {
      title: "Catat semua yang ada di rak Anda.",
      meta: "RAKEMORI · KATALOG PRIBADI",
      googlePlay: "Google Play",
      instagram: "Rakemori di Instagram",
    },
  },
  appShowcase: {
    back: "Kembali ke Rakemori",
    eyebrow: "Pratinjau langsung",
    heading: "Rakemori dari dekat",
    body: "Semua layar di sini berasal dari perpustakaan yang benar-benar dipakai. Masuk, jelajahi rak, buka detail rekaman, dan lihat siapa yang meminjam.",
    captions: {
      login: "Masuk",
      collection: "Rak Anda",
      detail: "Detail rekaman",
      borrowed: "Dipinjam",
    },
    ctaEyebrow: "Coba sendiri",
    ctaHeading: "Jelajahi isinya.",
    ctaBody: "Masuk dengan akun uji coba. Anda bisa menjelajahi perpustakaan sungguhan, tetapi tidak bisa mengubah apa pun di dalamnya.",
    username: "Username",
    password: "Password",
    ctaButton: "Coba sekarang",
  },
  collection: {
    searchPlaceholder: "Cari judul, artis, atau lagu",
    ownerTitle: (name: string) => `Koleksi ${name}`,
    all: "Semua",
    noItemsYet: "Belum ada rekaman",
    noMatches: "Tidak ditemukan",
    thisCollectionEmpty: "Koleksi ini masih kosong.",
    importCta: "Punya koleksi Discogs? Impor semuanya sekaligus",
    tryDifferentSearch: "Coba kata kunci atau filter lain.",
    sortAriaLabel: "Urutkan berdasarkan",
    sortRecentlyAdded: "Baru ditambahkan",
    sortArtist: "Artis",
    sortAlbumName: "Nama album",
    sortAlbumYear: "Tahun album",
  },
  artists: {
    countLabel: (n: number) => `${n} artis dalam koleksi ini`,
    noRecordsYet: "Belum ada rekaman",
    albumsOwned: "Album yang Dimiliki",
    noRecordsByArtist: "Belum ada rekaman dari artis ini.",
  },
  browse: {
    title: "Jelajahi berdasarkan lokasi",
    subtitle: "Lihat apa yang disimpan di mana.",
    noLocationsYet: "Belum ada lokasi yang dibuat.",
  },
  filterPage: {
    noRecordsGenre: "Belum ada rekaman dengan genre ini.",
    noRecordsCountry: "Belum ada rekaman dari negara ini.",
    noRecordsStyle: "Belum ada rekaman dengan gaya ini.",
  },
  itemDetail: {
    release: "Rilisan",
    tracklist: "Daftar Lagu",
    findingPreviews: "Mencari pratinjau…",
    findMissingPreviews: (n: number) => `Cari ${n} pratinjau yang hilang`,
    rescanPreviews: "Pindai ulang pratinjau",
    findingMissingPreviews: "Menanyakan ke layanan musik…",
    previewsFilled: (n: number) => `Menemukan ${n} pratinjau.`,
    previewsStillMissing: (n: number) => `${n} lagu belum punya pratinjau di iTunes atau Deezer.`,
    previewsRescanFailed: "Tidak bisa menghubungi layanan musik. Coba lagi sebentar.",
    refreshTracklist: "Muat ulang daftar lagu dari Discogs",
    refreshingTracklist: "Mengambil dari Discogs…",
    tracklistRefreshed: "Daftar lagu diperbarui dari Discogs.",
    tracklistUnchanged: "Sudah sesuai dengan Discogs.",
    tracklistRefreshFailed: "Tidak bisa menghubungi Discogs. Coba lagi sebentar.",
    listenOn: "Dengarkan di",
    companies: "Perusahaan",
    credits: "Kredit",
    photos: "Foto",
    notes: "Catatan",
    personalNotes: "Catatan pribadi",
    location: "Lokasi",
    price: "Harga",
    barcode: "Barcode",
    condition: "Kondisi",
    otherCopies: "Eksemplar lain",
    copy: "Eksemplar",
    mediaShort: "Media",
    sleeveShort: "Sampul",
    country: "Negara",
    genre: "Genre",
    style: "Gaya",
    released: "Tanggal rilis",
    thisRelease: "Rilisan ini",
    originalRelease: "Rilisan asli",
    label: "Label",
    originallyYear: (year: number) => `aslinya ${year}`,
  },
  itemActions: {
    edit: "Ubah",
    remove: "Hapus",
    removing: "Menghapus…",
    removeTitle: "Hapus rekaman ini?",
    removeMessage: "Rekaman ini akan dihapus dari koleksi Anda. Tindakan ini tidak dapat dibatalkan.",
    removeFailed: "Gagal menghapus. Coba lagi.",
  },
  itemForm: {
    uploading: "Mengunggah…",
    tapToChangeCover: "Ketuk untuk mengganti sampul",
    lookupLabel: "Cari di Discogs — album, artis, atau keduanya (cocok untuk rilisan tanpa barcode)",
    lookupPlaceholder: "mis. Sore Centralismo",
    search: "Cari",
    releaseFallback: "Rilisan",
    loading: "Memuat…",
    use: "Pakai",
    searchFailed: "Pencarian gagal — coba lagi sebentar lagi.",
    searchNoMatch: "Tidak ada yang cocok di Discogs — isi detail secara manual.",
    couldNotLoad: "Tidak dapat memuat rilisan itu — coba hasil lain.",
    filledIn: "Terisi dari Discogs — periksa dan perbaiki sebelum menyimpan.",
    barcodeLabel: "Barcode / UPC",
    scan: "Pindai",
    stop: "Berhenti",
    lookUp: "Cari data",
    noMatch: "Tidak ditemukan — isi detail secara manual.",
    titleLabel: "Judul",
    artistLabel: "Artis",
    typeLabel: "Jenis",
    thisReleaseLabel: "Rilisan ini",
    yearPlaceholder: "Tahun",
    originalReleaseLabel: "Rilisan asli",
    countryLabel: "Negara",
    releasedLabel: "Tanggal rilis",
    releasedPlaceholder: "mis. 2025-09-24",
    genreLabel: "Genre",
    genrePlaceholder: "Jazz, Rock…",
    styleLabel: "Gaya",
    stylePlaceholder: "Fusion…",
    priceLabel: "Harga",
    currencyLabel: "Mata uang",
    mediaConditionLabel: "Kondisi media",
    sleeveConditionLabel: "Kondisi sampul",
    notGraded: "Belum dinilai",
    locationLabel: "Lokasi",
    unassigned: "Belum ditempatkan",
    unassignedDefault: (label: string) => `Belum ditempatkan (default: ${label})`,
    labelsLabel: "Label (satu per baris: Nama – Nomor katalog)",
    labelsPlaceholder: "Alfa – MHJL 449",
    companiesLabel: "Perusahaan (satu per baris: Peran – Nama)",
    companiesPlaceholder: "Recorded At – Nama Studio",
    creditsLabel: "Kredit (satu per baris: Peran – Nama)",
    creditsPlaceholder: "Bass – Nama Artis",
    catalogNotesLabel: "Catatan rilisan (dari Discogs)",
    notesLabel: "Catatan pribadi",
    photosLabel: (n: number, max: number) => `Foto rekaman (${n}/${max})`,
    processing: "Memproses…",
    photosHint: "Foto piringan, sampul, atau selipan aslinya. Foto dikonversi ke WebP agar hemat ruang.",
    addPhoto: "Tambah",
    tracklistHeading: "Daftar Lagu",
    addTrack: "Tambah lagu",
    trackTitlePlaceholder: "Judul lagu",
    trackArtistPlaceholder: "Artis (album kompilasi)",
    trackDurationPlaceholder: "mm:ss",
    findPreviews: "Cari pratinjau",
    findingPreviews: "Mencari pratinjau…",
    previewsNone: "Pratinjau tidak ditemukan untuk daftar lagu ini.",
    saving: "Menyimpan…",
    saveChanges: "Simpan perubahan",
    addToCollection: "Tambah ke koleksi",
    readOnlyNotice: "Ini akun uji coba baca-saja — Anda bisa mencari dan memindai, tapi perubahan tidak dapat disimpan.",
    saveFailed: "Gagal menyimpan rekaman",
    uploadFailed: "Gagal mengunggah foto",
    photoUploadFailed: "Gagal mengunggah foto — coba lagi.",
    photoProcessFailed: "Tidak dapat memproses foto — coba gambar lain.",
    maxPhotos: (max: number) => `Anda dapat menambahkan hingga ${max} foto.`,
    tooManyPhotos: (max: number, skipped: number) => `Maksimal ${max} foto — ${skipped} dilewati.`,
    viewExisting: "Lihat di koleksi Anda",
    alreadyInLibrary: "Sudah ada di perpustakaan Anda",
    batchScan: "Batch",
    identifySleeve: "Kenali dari sampul",
    sleeveCatnoMatches: (catno: string) => `Hasil untuk nomor katalog ${catno}`,
    sleeveTitleMatches: "Perkiraan terbaik dari teks sampul",
    sleeveNoText: "Tidak ada teks yang terbaca di foto itu.",
    sleeveNoMatch: "Tidak ada hasil Discogs untuk sampul itu.",
    sleeveCameraFailed: "Tidak dapat membuka kamera.",
    matchRunout: "Cocokkan runout",
    runoutMatched: (matrix: string) => `Runout cocok — ${matrix}. Hasil terbaik di urutan pertama.`,
    runoutNoMatch: "Tidak ada runout yang cocok di hasil ini.",
    runoutFailed: "Pencocokan runout gagal — coba lagi sebentar lagi.",
    addAnyway: "Tetap tambahkan \u2014 eksemplar berbeda",
    willAddCopy: "Akan ditambahkan sebagai eksemplar kedua.",
  },
  books: {
    title: "Buku",
    searchPlaceholder: "Cari judul, penulis, atau seri",
    booksCount: (n: number) => `${n} buku`,
    all: "Semua",
    noBooksYet: "Belum ada buku",
    thisShelfEmpty: "Koleksi buku Anda masih kosong.",
    noMatches: "Tidak ditemukan",
    tryDifferentSearch: "Coba kata kunci atau filter lain.",
    noBooksHereYet: "Belum ada buku di sini.",
    formats: {
      HARDCOVER: "Sampul keras",
      PAPERBACK: "Sampul lunak",
      OTHER: "Lainnya",
    },
    sortAuthor: "Penulis",
    sortTitle: "Judul",
    sortYear: "Tahun",
    addTitle: "Tambah buku",
    editTitle: (title: string) => `Ubah ${title}`,
    detail: {
      publisher: "Penerbit",
      published: "Diterbitkan",
      firstPublished: "Terbit pertama",
      source: "Sumber",
      originalTitle: "Judul asli",
      language: "Bahasa",
      translator: "Penerjemah",
      edition: "Edisi",
      series: "Seri",
      seriesVolume: "Volume",
      pages: "Halaman",
      isbn: "ISBN",
      subjects: "Subjek",
      description: "Deskripsi",
      details: "Detail",
      photos: "Foto",
      personalNotes: "Catatan pribadi",
      location: "Lokasi",
      price: "Harga",
    },
    removeTitle: "Hapus buku ini?",
    removeMessage: "Buku ini akan dihapus dari koleksi Anda. Tindakan ini tidak dapat dibatalkan.",
    noBooksSubject: "Belum ada buku dengan subjek ini.",
    form: {
      lookupLabel: "Cari di Open Library / Google Books berdasarkan judul, penulis, atau keduanya",
      lookupPlaceholder: "mis. Bumi Manusia",
      search: "Cari",
      use: "Pakai",
      loading: "Memuat…",
      filledIn: "Terisi dari hasil pencarian. Periksa dan perbaiki sebelum menyimpan.",
      noMatch: "Tidak ditemukan. Isi detail secara manual.",
      searchFailed: "Pencarian gagal. Coba lagi sebentar lagi.",
      couldNotLoad: "Tidak dapat memuat hasil itu. Coba yang lain.",
      isbnLabel: "ISBN / barcode",
      scan: "Pindai",
      stop: "Berhenti",
      lookUp: "Cari data",
      batchScan: "Batch",
      identifyCover: "Kenali dari sampul",
      coverNoText: "Tidak ada teks yang terbaca di foto itu.",
      coverIsbnMatch: (isbn: string) => `Ditemukan lewat ISBN ${isbn} — ketuk untuk memakainya`,
      coverTitleMatches: "Tebakan terbaik dari teks sampul",
      coverNoMatch: "Tidak ada yang cocok untuk sampul itu.",
      titleLabel: "Judul",
      subtitleLabel: "Subjudul",
      authorLabel: "Penulis",
      formatLabel: "Format",
      publishedYearLabel: "Diterbitkan (tahun)",
      publisherLabel: "Penerbit",
      languageLabel: "Bahasa",
      pagesLabel: "Halaman",
      subjectsLabel: "Subjek",
      subjectsPlaceholder: "Sejarah, Fiksi…",
      descriptionLabel: "Deskripsi",
      collectorHeading: "Detail kolektor",
      collectorHint: "Semua opsional, untuk mencatat edisi tertentu.",
      originalTitleLabel: "Judul asli",
      originalYearLabel: "Terbit pertama (tahun)",
      translatorLabel: "Penerjemah",
      editionLabel: "Edisi / cetakan",
      editionPlaceholder: "mis. Edisi ke-2, cetakan ke-3",
      seriesLabel: "Seri",
      seriesVolumeLabel: "Volume",
      priceLabel: "Harga",
      currencyLabel: "Mata uang",
      locationLabel: "Lokasi",
      unassigned: "Belum ditempatkan",
      unassignedDefault: (label: string) => `Belum ditempatkan (default: ${label})`,
      notesLabel: "Catatan pribadi",
      photosLabel: (n: number, max: number) => `Foto buku (${n}/${max})`,
      photosHint: "Foto buku fisiknya: kondisi sampul, tanda tangan, kotak. Foto dikonversi ke WebP agar hemat ruang.",
      processing: "Memproses…",
      addPhoto: "Tambah",
      uploading: "Mengunggah…",
      tapToChangeCover: "Ketuk untuk mengganti sampul",
      viewExisting: "Lihat di koleksi Anda",
      saveChanges: "Simpan perubahan",
      addToCollection: "Tambah ke koleksi",
      saving: "Menyimpan…",
      saveFailed: "Gagal menyimpan buku",
      uploadFailed: "Gagal mengunggah foto",
      photoProcessFailed: "Tidak dapat memproses foto. Coba gambar lain.",
      tooManyPhotos: (max: number, skipped: number) => `Maksimal ${max} foto, sehingga ${skipped} tidak terunggah.`,
      maxPhotos: (max: number) => `Anda dapat menambahkan hingga ${max} foto.`,
    },
  },
  authors: {
    booksOwned: "Buku yang Dimiliki",
    noBooksByAuthor: "Belum ada buku dari penulis ini.",
  },
  people: {
    all: "Semua",
    artists: "Artis",
    authors: "Penulis",
    searchPlaceholder: "Cari artis & penulis",
    peopleCount: (n: number) => `${n} orang dalam koleksi ini`,
    artistsCount: (n: number) => `${n} artis dalam koleksi ini`,
    authorsCount: (n: number) => `${n} penulis dalam koleksi ini`,
    artistTag: "Artis",
    authorTag: "Penulis",
    noPeopleYet: "Belum ada artis atau penulis",
  },
  confirmDialog: {
    cancel: "Batal",
    confirm: "Konfirmasi",
  },
  settings: {
    eyebrow: "Akun",
    title: "Pengaturan",
    profile: "Profil",
    appearance: "Tampilan",
    library: "Perpustakaan",
    locations: "Lokasi",
    history: "Riwayat",
    borrowed: "Peminjaman",
    language: "Bahasa",
    help: "Bantuan",
    locationsDescription:
      "Susun struktur bertingkat tempat penyimpanan fisik, misalnya Rumah → Ruang Keluarga → Lemari TV → Rak A1.",
  },
  profile: {
    lastCredential: "Ini satu-satunya cara Anda bisa masuk. Buat kata sandi dulu sebelum melepasnya.",
    usernameLabel: "Nama pengguna",
    usernameHint: (username: string) => `Alamat pustaka Anda: rakemori.com/${username}`,
    nameLabel: "Nama",
    nameHint: "Tampil di samping foto Anda dan di pustaka yang Anda bagikan.",
    usernameTaken: "Nama pengguna itu sudah dipakai.",
    usernameInvalid: "Hanya huruf, angka, dan tanda hubung, dan bukan kata yang dicadangkan.",
    renameTitle: "Ubah nama pengguna?",
    renameMessage: (from: string, to: string) =>
      `Pustaka Anda pindah dari rakemori.com/${from} ke rakemori.com/${to}. Tautan ke alamat lama tetap berfungsi dan mengarah ke alamat baru, dan tidak ada orang lain yang bisa memakai nama lama itu.`,
    renameConfirm: "Ubah nama pengguna",
    saveFailed: "Profil tidak bisa disimpan.",
  },
  history: {
    description: "Setiap rekaman dan buku yang Anda tambahkan atau hapus, terbaru lebih dulu. Hanya Anda yang bisa melihat ini.",
    added: "Ditambahkan",
    removed: "Dihapus",
    emptyState: "Belum ada apa-apa di sini. Tambah atau hapus rekaman atau buku, dan akan muncul di daftar ini.",
    reasonSold: (price: string) => `Terjual · Rp ${price}`,
    reasonSoldNoPrice: "Terjual",
    reasonExchanged: "Ditukar dengan",
    reasonExchangedUnknown: "rekaman yang telah dihapus",
  },
  removalReason: {
    title: "Kenapa ini dihapus?",
    missing: "Hilang",
    trash: "Dibuang",
    sold: "Terjual",
    exchange: "Ditukar",
    others: "Lainnya",
    skip: "Lewati",
    soldPriceLabel: "Harga (opsional)",
    othersPlaceholder: "Tambahkan catatan (opsional)",
    exchangeSearchPlaceholder: "Cari di koleksi Anda…",
    exchangeSearching: "Mencari…",
    exchangeNoResults: "Tidak ditemukan. Tambahkan yang baru sebagai gantinya.",
    exchangeAddNewRecord: "+ Tambah rekaman baru sebagai gantinya",
    exchangeAddNewBook: "+ Tambah buku baru sebagai gantinya",
    exchangeSelected: (title: string) => `Dipilih: ${title}`,
    exchangeChange: "Ubah",
    exchangeResumeFailed:
      "Rekaman berhasil ditambahkan, tapi yang lama tidak dapat dihapus secara otomatis. Hapus secara manual jika perlu.",
    exchangeResumeDismiss: "Tutup",
  },
  borrow: {
    dialogTitle: "Siapa yang meminjam ini?",
    nameLabel: "Nama peminjam",
    namePlaceholder: "mis. Sarah",
    phoneLabel: "Nomor telepon (opsional)",
    phonePlaceholder: "mis. 08123456789",
    searching: "Mencari…",
    cancel: "Batal",
    confirm: "Konfirmasi",
    markReturned: "Tandai dikembalikan",
    marking: "Menandai…",
    borrowedDaysAgo: (n: number) => (n === 0 ? "Dipinjam hari ini" : `Dipinjam ${n} hari yang lalu`),
    description: "Rekaman dan buku yang sedang dipinjam, dari yang paling lama. Hanya Anda yang bisa melihat ini.",
    emptyState: "Tidak ada yang sedang dipinjam saat ini.",
    borrowRequiredError: "Info peminjam wajib diisi. Silakan coba lagi.",
  },
  appearance: {
    description: "Pilih tampilan aplikasi di perangkat ini. Auto mengikuti pengaturan mode terang/gelap sistem Anda.",
    light: "Terang",
    dark: "Gelap",
    auto: "Auto",
  },
  language: {
    description: "Pilih bahasa tampilan aplikasi.",
    english: "English",
    indonesian: "Bahasa Indonesia",
  },
  share: {
    loading: "Memuat…",
    heading: "Bagikan ke orang lain",
    description:
      "Beri akses baca ke perpustakaan privat Anda untuk seseorang yang punya akun Rakemori. Mereka masuk dengan akun mereka sendiri, jadi tidak ada kata sandi tambahan, dan Anda bisa mencabutnya kapan saja.",
    usernamePlaceholder: "Nama pengguna mereka",
    shareButton: "Bagikan",
    sharing: "Membagikan…",
    emptyShares: "Belum dibagikan ke siapa pun.",
    addFailed: "Tidak dapat membagikan ke orang tersebut.",
    revokeTitle: (who: string) => `Berhenti berbagi dengan ${who}?`,
    revokeMessage: "Mereka akan langsung kehilangan akses ke perpustakaan Anda. Anda bisa membagikannya lagi nanti.",
    revokeConfirm: "Berhenti berbagi",
    sharedWithYouHeading: "Dibagikan kepada Anda",
    sharedWithYouDescription: "Perpustakaan privat yang dibagikan orang lain kepada Anda.",
    someonesCollection: (who: string) => `Koleksi ${who}`,
  },
  backup: {
    title: "Cadangkan & pulihkan",
    description:
      "Unduh cadangan lengkap koleksi Anda sebagai satu berkas, mencakup setiap rekaman dan buku, fotonya, dan struktur lokasi Anda. Simpan di tempat yang aman, atau gunakan untuk memindahkan koleksi Anda ke akun lain.",
    download: "Unduh cadangan",
    importHeading: "Pulihkan dari cadangan",
    mergeLabel: "Gabungkan",
    replaceLabel: "Ganti",
    mergeHint:
      "Menambahkan rekaman dari cadangan ke koleksi Anda saat ini, melewati yang sudah Anda miliki. Menggunakan kembali lokasi yang cocok alih-alih menggandakannya.",
    replaceHint:
      "Menghapus koleksi Anda saat ini dan menggantinya sepenuhnya dengan cadangan. Tidak dapat dibatalkan.",
    chooseFile: "Pilih berkas cadangan",
    importButton: "Pulihkan",
    importing: "Memulihkan…",
    replaceConfirmTitle: "Ganti seluruh koleksi Anda?",
    replaceConfirmMessage:
      "Setiap rekaman dan lokasi yang Anda miliki sekarang akan dihapus permanen dan diganti dengan isi cadangan ini. Tindakan ini tidak dapat dibatalkan.",
    replaceConfirmLabel: "Ganti semuanya",
    resultSummary: (records: number, books: number, skipped: number, images: number) =>
      `Memulihkan ${records} rekaman dan ${books} buku` +
      (skipped > 0 ? `, melewati ${skipped} duplikat` : "") +
      `, dan ${images} gambar.`,
    error: "Impor gagal. Silakan periksa berkas dan coba lagi.",
  },
  help: {
    intro: "Panduan singkat untuk semua fitur di Rakemori. Ketuk sebuah bagian untuk membukanya.",
    sections: [
      {
        title: "Memulai",
        body: "Rakemori membantu Anda mengatalogkan dan menemukan CD, kaset, dan piringan hitam Anda. Tambahkan rekaman secara manual, dengan memindai barcode, atau dengan mencari di Discogs. Sampul album, daftar lagu, dan detail lainnya akan terisi otomatis saat ditemukan kecocokan.",
      },
      {
        title: "Menambah & mengubah rekaman",
        body: "Ketuk Tambah di halaman Koleksi untuk membuat rekaman baru. Pindai barcode atau cari di Discogs berdasarkan album/artis untuk mengisi detail otomatis, atau isi semuanya secara manual. Dari halaman rekaman mana pun, gunakan Ubah untuk mengubah detail atau Hapus untuk menghapusnya. Menghapus rekaman tidak dapat dibatalkan.",
      },
      {
        title: "Menghapus rekaman atau buku",
        body: "Saat Anda menghapus rekaman atau buku, Anda akan ditanya alasannya: Hilang, Dibuang, Terjual, Ditukar dengan item lain, atau Lainnya. Alasan ini disimpan ke Riwayat aktivitas Anda. Memilih Ditukar memungkinkan Anda mencari di koleksi Anda apa yang menjadi gantinya, atau menambah rekaman/buku baru saat itu juga; yang lama otomatis terhapus setelah yang baru disimpan.",
      },
      {
        title: "Riwayat aktivitas",
        body: "Pengaturan → Riwayat menyimpan catatan pribadi setiap rekaman atau buku yang Anda tambahkan atau hapus, terbaru lebih dulu, hanya terlihat oleh Anda. Entri yang ditambahkan tertaut kembali ke rekaman tersebut; entri yang dihapus tetap menunjukkan apa yang pernah ada, meski sudah tidak ada lagi.",
      },
      {
        title: "Mencari, menyaring & mengurutkan",
        body: "Kolom pencarian di halaman Koleksi menyaring hasil saat Anda mengetik. Pencarian mencocokkan judul, artis, dan nama lagu. Gunakan tombol Semua/CD/Kaset/Vinil untuk menyaring berdasarkan format, dan ikon urutkan untuk mengurutkan berdasarkan baru ditambahkan, artis, nama album, atau tahun.",
      },
      {
        title: "Artis & penulis",
        body: "Tab Artis adalah satu halaman untuk menjelajahi semua orang di perpustakaan Anda: gunakan tombol Semua / Artis / Penulis untuk berpindah, dan kolom pencarian untuk menyaring nama sambil mengetik. Kartu artis menampilkan foto dan biografi dari Discogs; kartu penulis dari Open Library. Ketuk siapa pun untuk melihat profilnya beserta semua album atau buku miliknya yang Anda punya.",
      },
      {
        title: "Buku",
        body: "Tab Buku mengatalogkan buku fisik Anda berdampingan dengan musik. Tambahkan dengan memindai barcode ISBN (pemindai yang sama dengan rekaman, karena barcode buku adalah ISBN-nya) atau mencari judul/penulis; detail, sampul, dan subjek terisi dari Open Library, dengan Google Books sebagai cadangan. Kolom kolektor (edisi, penerjemah, seri, judul/tahun asli) semuanya opsional. Saring berdasarkan Sampul keras/Sampul lunak, urutkan, dan ketuk subjek pada buku mana pun untuk melihat semua koleksi Anda dengan subjek itu.",
      },
      {
        title: "Lokasi & Jelajahi",
        body: "Atur tempat penyimpanan fisik rekaman di Pengaturan → Lokasi. Buat struktur bertingkat seperti Rumah → Ruang Keluarga → Lemari TV → Rak A1. Tab Jelajahi memungkinkan siapa saja menjelajahi koleksi Anda berdasarkan struktur lokasi yang sama. Atur lokasi default agar rekaman baru ditempatkan secara otomatis di tempat yang sesuai.",
      },
      {
        title: "Meminjamkan rekaman atau buku",
        body: "Ubah Lokasi rekaman atau buku ke \"Borrowed\" lalu simpan. Anda akan diminta nama peminjam (terisi otomatis dari siapa pun yang pernah meminjam di perpustakaan ini) dan nomor teleponnya. Pengaturan → Peminjaman menampilkan semua yang sedang dipinjam, dari yang paling lama, dengan tombol \"Tandai dikembalikan\" sekali ketuk yang mengembalikannya ke lokasi asal. Mengubah Lokasi item yang sedang dipinjam ke selain \"Borrowed\" lewat formulir ubah biasa juga otomatis menandainya sebagai dikembalikan.",
      },
      {
        title: "Menyembunyikan lokasi dari publik",
        body: "Di Pengaturan → Lokasi, ketuk ikon mata di sebelah lokasi mana pun untuk menyembunyikannya, beserta semua yang disimpan di dalamnya, dari pengunjung publik dan tamu, meskipun perpustakaan Anda diatur ke Publik. Hanya Anda (dan admin situs) yang tetap dapat melihatnya. Menyembunyikan lokasi induk juga menyembunyikan semua yang ada di dalamnya.",
      },
      {
        title: "Membagikan perpustakaan Anda",
        body: "Di Pengaturan → Perpustakaan, atur perpustakaan Anda ke Publik agar siapa saja dapat melihatnya di URL profil Anda tanpa masuk. Atau tetap Privat dan bagikan ke orang tertentu: masukkan nama pengguna siapa pun yang memiliki akun Rakemori, dan mereka dapat melihat perpustakaan Anda saat masuk sebagai diri mereka sendiri. Tidak ada kata sandi tambahan, dan Anda bisa mencabut akses per orang kapan saja. Perpustakaan yang dibagikan orang lain kepada Anda muncul di bagian \"Dibagikan kepada Anda\" di tempat yang sama. Pengaturan tampilan publik mengontrol apakah harga dan lokasi terlihat oleh orang selain Anda.",
      },
      {
        title: "Tampilan & bahasa",
        body: "Pengaturan → Tampilan memungkinkan Anda beralih antara Terang, Gelap, atau Auto (mengikuti perangkat Anda). Pengaturan → Bahasa mengganti bahasa aplikasi antara Inggris dan Bahasa Indonesia secara instan. Sebelum Anda memilih, pengunjung baru akan melihat bahasa yang sesuai dengan preferensi peramban mereka.",
      },
      {
        title: "Menjaga data Discogs tetap segar",
        body: "Pengaturan → Perpustakaan memiliki tombol \"Pindai ulang perpustakaan dari Discogs\" yang mengambil ulang judul, artis, tahun, genre, label, kredit, dan lainnya untuk setiap rekaman yang cocok dengan Discogs. Ini berguna untuk melengkapi rekaman yang ditambahkan sebelum kolom-kolom ini ada. Ini hanya memengaruhi perpustakaan Anda sendiri dan berjalan bertahap agar tetap dalam batas kecepatan Discogs.",
      },
      {
        title: "Cadangkan & pulihkan",
        body: "Pengaturan → Perpustakaan → Cadangkan & pulihkan mengunduh seluruh koleksi Anda sebagai satu berkas .zip, mencakup rekaman, buku, foto, dan struktur lokasi. Untuk mengembalikannya, pilih berkas lalu pilih Gabungkan (menambahkan rekaman dan buku cadangan ke yang Anda miliki, melewati duplikat) atau Ganti (menghapus koleksi Anda dan memulihkan cadangan persis). Ganti tidak dapat dibatalkan, jadi simpan berkas cadangan Anda di tempat yang aman.",
      },
    ],
  },
  install: {
    iphone: {
      metaTitle: "Pasang di iPhone",
      metaDescription:
        "Dua cara memasang Rakemori di iPhone: aplikasinya lewat AltStore dan AltServer, atau web app dari Safari dengan Tambah ke Layar Utama.",
      back: "Kembali ke Rakemori",
      eyebrow: "iPhone",
      title: "Pasang Rakemori di iPhone Anda",
      intro:
        "Rakemori belum ada di App Store. Sampai ada, tersedia dua cara: pasang aplikasinya lewat AltStore, yang butuh komputer, atau tambahkan web app dari Safari, yang tidak. Keduanya masuk ke perpustakaan yang sama.",
      compare: {
        heading: "Pilih yang mana?",
        appColumn: "Aplikasi AltStore",
        webColumn: "Web app Safari",
        rows: [
          { label: "Butuh komputer", app: "Ya, untuk pemasangan pertama dan setiap pembaruan", web: "Tidak" },
          { label: "Kedaluwarsa", app: "Setiap tujuh hari dengan Apple ID gratis", web: "Tidak pernah" },
          { label: "Masuk dengan Google", app: "Bisa", web: "Bisa" },
          { label: "Pindai barcode", app: "Bawaan, dengan kamera ponsel", web: "Lewat kamera browser" },
          { label: "Bisa dipakai offline", app: "Perpustakaan Anda, setelah dimuat sekali", web: "Perpustakaan Anda, setelah dimuat sekali" },
        ],
        advice:
          "Kalau ragu, pilih web app. Prosesnya semenit dan tidak ada yang perlu dirawat. AltStore bisa menyusul kapan saja kalau Anda ingin pemindai bawaan.",
      },
      app: {
        heading: "Cara 1: aplikasinya, lewat AltStore",
        intro:
          "AltStore memasang aplikasi yang tidak ada di App Store dengan menandatanganinya memakai Apple ID Anda sendiri. Penandatanganan itu terjadi di Mac atau PC Windows yang menjalankan AltServer, jadi Anda perlu komputer di dekat Anda untuk pemasangan pertama. Siapkan kabel dan kata sandi Apple ID. Waktunya sekitar sepuluh menit.",
        steps: [
          {
            title: "Pasang AltServer di komputer",
            body: [
              "Unduh dari [altstore.io](https://altstore.io). Di Mac, seret AltServer ke Applications lalu buka. Ikonnya muncul di bilah menu, bukan di Dock. Di Windows, jalankan pemasangnya, dan pastikan iTunes dan iCloud diunduh dari situs Apple sendiri, bukan dari Microsoft Store. Kalau tidak, AltServer tidak akan mengenali ponsel Anda.",
            ],
          },
          {
            title: "Sambungkan iPhone dan pasang AltStore",
            body: [
              "Sambungkan ponsel dengan kabel dan ketuk [[Percayai]] kalau diminta. Klik ikon AltServer di bilah menu (Mac) atau system tray (Windows), pilih [[Install AltStore]], lalu pilih iPhone Anda. Masuk dengan Apple ID saat diminta. AltServer memakainya hanya untuk menandatangani aplikasi. Datanya tidak pernah sampai ke Rakemori.",
            ],
          },
          {
            title: "Percayai aplikasinya di iPhone",
            body: [
              "AltStore sekarang ada di Layar Utama, tetapi iOS tidak mau membukanya sebelum Anda mengizinkan. Buka [[Pengaturan]] → [[Umum]] → [[VPN & Manajemen Perangkat]], ketuk entri dengan Apple ID Anda, lalu ketuk [[Percayai]].",
              "Kalau ponsel meminta [[Mode Pengembang]] dinyalakan, nyalakan saja: [[Pengaturan]] → [[Privasi & Keamanan]] → [[Mode Pengembang]], lalu mulai ulang saat diminta. Semua aplikasi sideload memerlukan ini.",
            ],
          },
          {
            title: "Tambahkan sumber Rakemori",
            body: [
              "Buka AltStore, masuk ke tab [[Sources]], ketuk [[+]] dan tempelkan alamat ini persis apa adanya:",
              "`{sourceUrl}`",
              "Ketuk [[Add Source]] dan Rakemori muncul di daftar.",
            ],
          },
          {
            title: "Pasang Rakemori",
            body: [
              "Masuk ke [[Browse]], cari Rakemori dan ketuk [[Free]]. AltStore menandatangani aplikasi dengan Apple ID Anda lalu memasangnya. Biarkan ponsel tetap di Wi-Fi yang sama dengan komputer selama proses berjalan. Begitu ikonnya muncul di Layar Utama, selesai.",
              "Masuk seperti biasa, lewat Google atau dengan nama pengguna dan kata sandi.",
            ],
          },
        ],
        refresh: {
          title: "Agar tetap hidup",
          body: [
            "Apple ID gratis menandatangani aplikasi untuk **tujuh hari**. Setelah itu aplikasi tidak mau dibuka sampai diperbarui. AltStore melakukannya sendiri di latar belakang selama ponsel berada di Wi-Fi yang sama dengan komputer yang menjalankan AltServer, jadi membiarkan AltServer tetap berjalan biasanya cukup. Anda juga bisa membuka AltStore dan mengetuk [[Refresh All]]. Akun Apple Developer berbayar memperpanjang tujuh hari itu menjadi setahun.",
          ],
        },
        caveats: {
          title: "Perlu diketahui",
          body: [
            "Apple ID gratis menampung **tiga** aplikasi sideload sekaligus, dihitung dari semua sumber yang Anda tambahkan. Rakemori memakai satu slot.",
            "Tautan ke rakemori.com terbuka di Safari, bukan di aplikasi, jadi tautan pustaka yang dibagikan tidak langsung membuka aplikasinya. Masuk lewat Google tidak terpengaruh dan tetap berfungsi di sini.",
            "Masuk dengan Apple belum tersedia, baik di aplikasi maupun di web.",
            "Kalau nanti Anda memasang versi App Store, hapus dulu yang dari AltStore. iOS tidak mau menyimpan keduanya.",
            "Memerlukan iOS {minOs} atau lebih baru.",
          ],
        },
      },
      web: {
        heading: "Cara 2: web app, dari Safari",
        intro:
          "Rakemori memang dibuat untuk berjalan sebagai web app. Ditambahkan dari Safari, ia punya ikon sendiri, terbuka layar penuh tanpa bilah alamat, dan tetap dalam keadaan masuk. Tidak ada yang kedaluwarsa dan tidak ada yang perlu diperbarui.",
        steps: [
          {
            title: "Buka halaman masuk di Safari",
            body: [
              "Harus Safari. Chrome dan browser lain tidak bisa menambahkan web app ke Layar Utama. Buka:",
              "`{loginUrl}`",
              "Masuk seperti biasa, termasuk lewat Google atau Apple.",
            ],
          },
          {
            title: "Ketuk Bagikan",
            body: ["Ikonnya kotak dengan panah ke atas, di bilah alat Safari."],
          },
          {
            title: "Pilih Tambah ke Layar Utama",
            body: [
              "Gulir share sheet ke bawah sampai menemukan [[Tambah ke Layar Utama]]. Kalau tidak ada, ketuk [[Edit Tindakan]] di bagian bawah dan nyalakan.",
            ],
          },
          {
            title: "Nyalakan Buka sebagai App Web",
            body: [
              "Di layar berikutnya, pastikan [[Buka sebagai App Web]] (Open as Web App) menyala. Kalau mati, yang Anda dapat hanya bookmark yang terbuka di Safari. Kalau menyala, Rakemori terbuka sebagai aplikasi sendiri. iOS versi lama tidak menampilkan tombol ini dan langsung memperlakukannya sebagai web app.",
            ],
          },
          {
            title: "Ketuk Tambah",
            body: [
              "Rakemori sekarang ada di Layar Utama. Buka dari sana, bukan dari Safari, dan ia berjalan layar penuh dengan sesi Anda yang sudah ada.",
            ],
          },
        ],
        fallback: {
          title: "Kalau tetap terbuka di Safari",
          body: [
            "Tombolnya mati. Tahan ikonnya, pilih [[Hapus Bookmark]], lalu ulangi dari langkah 1 dengan [[Buka sebagai App Web]] menyala.",
          ],
        },
      },
      help: {
        heading: "Masih macet?",
        body: "Kirim email ke [hello@rakemori.com](mailto:hello@rakemori.com) dengan model iPhone, versi iOS, dan cara mana yang sedang Anda ikuti.",
      },
    },
  },
};
